import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Eye,
  RefreshCw,
  Filter,
  Package,
  AlertCircle,
  ExternalLink,
  Sparkles,
  Bot,
  Ban,
  Zap,
  RotateCcw,
  BarChart3,
  FileSearch,
  Save,
  ArrowLeft,
  CheckSquare,
  Square,
  Download,
  Settings,
  Trash2,
  X,
  Database
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { cleanProductUrl } from '@/utils/url-cleaner';
import { API_URL } from '@/config/api';
import { ContentOpportunities } from '@/components/content-opportunities/ContentOpportunities';
import { ExportMissingFieldsDialog } from '@/components/enrichment/ExportMissingFieldsDialog';
import { ValidationToolsTab } from '@/components/enrichment/ValidationToolsTab';
import { FeedSyncPanel } from '@/components/enrichment/FeedSyncPanel';
import { AeoStalenessWarning } from '@/components/enrichment/AeoStalenessWarning';

interface SuspiciousAttribute {
  value: unknown;
  unit?: string;
  source?: string;
  confidence?: number;
  needs_review?: boolean;
  validation_note?: string;
}

interface SuspiciousProduct {
  id: string;
  name: string;
  brand: string;
  category: string;
  price: string;
  product_url?: string;
  description?: string;
  suspicious_attributes: Record<string, SuspiciousAttribute>;
  all_enriched_attributes: Record<string, SuspiciousAttribute>;
}

interface Stats {
  total_enriched_products: number;
  products_needing_review: number;
  review_percentage: string;
}

interface Pagination {
  total: number;
  pageSize: number;
  offset: number;
}

interface FilterInfo {
  availableAttributeTypes: Array<{ name: string; count: number }>;
  currentAttributeType: string | null;
}

interface SimilarityMatch {
  suggestedValue: string;
  similarity: number;
}

interface AiSuggestion {
  attributeName: string;
  value: string;
  loading: boolean;
  model?: string;
  source?: string;
  sourceTitle?: string;
  searchedWeb?: boolean;
  numericValue?: number | null;
  unit?: string | null;
  similarityMatches?: SimilarityMatch[];
  totalWithValue?: number;
}

interface SiteAttributeValue {
  id: number;
  attribute_name: string;
  product_category: string | null;
  accepted_values: string[];
  canonical_values: string[] | null; // Sector-level allowed values for context
  created_at: string;
  updated_at: string;
}

// Research mode interfaces
interface MissingSpec {
  spec: string;
  displayName: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
  aliases: string[];
}

interface ProductSearchResult {
  id: string;
  name: string;
  category: string;
  price: string;
  imageUrl?: string;
  isEnriched: boolean;
}

interface StaleAttribute {
  name: string;
  oldValue: string;
  newValue: string;
  reason?: string;
}

interface ProductWithMissingSpecs {
  productId: string;
  productName: string;
  brand: string;
  category: string;
  normalizedCategory: string;
  priceTier: string;
  productUrl?: string;
  missingSpecs: MissingSpec[];
  existingSpecs: Record<string, { value: unknown; unit?: string; source?: string }>;
  completenessScore: number;
  requiredCount: number;
  foundCount: number;
  // AEO status - used to show appropriate actions when specs complete but AEO needs regeneration
  aeoStatus?: string | null;
  aeoGenerationId?: string | null;
  // AEO staleness info
  aeoUpdatedAt?: string | null;
  kbUpdatedAt?: string | null;
  aeoIsStale?: boolean;
  staleAttributes?: StaleAttribute[];
  staleContentSections?: string[];
}

interface NeedsResearchProduct {
  id: string;
  aeoId: string;
  productName: string;
  brand: string;
  category: string;
  price: string;
  productUrl?: string;
  imageUrl?: string;
  missingSpecs: string[];
  updatedAt: string;
}

// Attribute with full provenance
interface OverwrittenFrom {
  value: string;
  source: string;
  overwrittenAt?: string;
  reason?: string;
}

interface FullAttribute {
  name: string;
  value: unknown;
  unit: string | null;
  origin: 'feed' | 'ai_extracted' | 'ai_researched' | 'manual' | 'unknown';
  confidence: number;
  sourceText: string | null;
  sourceUrl: string | null;
  lastModified: string | null;
  modifiedBy: string | null;
  researchedVia?: string | null;
  overwrittenFrom?: OverwrittenFrom | null;
}

interface AttributesSummary {
  feed: number;
  aiExtracted: number;
  aiResearched: number;
  manual: number;
  unknown: number;
  total: number;
}

// All Attributes Section - shows full provenance of product attributes
function AllAttributesSection({ productId }: { productId: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attributes, setAttributes] = useState<FullAttribute[]>([]);
  const [summary, setSummary] = useState<AttributesSummary | null>(null);

  useEffect(() => {
    if (!productId || !isOpen) return;

    const fetchAttributes = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authFetch(`/api/product-enrichment/${productId}/attributes-full`);

        if (!response.ok) {
          throw new Error('Failed to fetch attributes');
        }

        const data = await response.json();

        if (data.success) {
          setAttributes(data.data.attributes || []);
          setSummary(data.data.summary || null);
        } else {
          throw new Error(data.error || 'Unknown error');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load attributes');
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, [productId, isOpen]);

  const originLabels: Record<string, { label: string; color: string; bg: string }> = {
    feed: { label: 'Feed', color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/40' },
    ai_extracted: { label: 'AI Extracted', color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/40' },
    ai_researched: { label: 'AI Researched', color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/40' },
    manual: { label: 'Manual', color: 'text-orange-600', bg: 'bg-orange-100 dark:bg-orange-900/40' },
    unknown: { label: 'Unknown', color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-900/40' },
  };

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Group attributes by origin
  const groupedAttributes = attributes.reduce((acc, attr) => {
    const origin = attr.origin || 'unknown';
    if (!acc[origin]) acc[origin] = [];
    acc[origin].push(attr);
    return acc;
  }, {} as Record<string, FullAttribute[]>);

  return (
    <div className="border-t pt-4 mt-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-2 px-3 rounded-md hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">All Attributes with Origin Tracking</span>
          {summary && (
            <Badge variant="secondary" className="ml-2">
              {summary.total} total
            </Badge>
          )}
        </div>
        {isOpen ? (
          <ChevronRight className="h-4 w-4 text-muted-foreground rotate-90 transition-transform" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform" />
        )}
      </button>

      {isOpen && (
        <div className="mt-3 space-y-4 pl-2">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-sm text-red-500 py-2">{error}</div>
          ) : attributes.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">
              No attributes found for this product.
            </div>
          ) : (
            <>
              {/* Summary badges */}
              {summary && (
                <div className="flex flex-wrap gap-2 pb-2">
                  {summary.feed > 0 && (
                    <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950">
                      Feed: {summary.feed}
                    </Badge>
                  )}
                  {summary.aiExtracted > 0 && (
                    <Badge variant="outline" className="text-xs bg-purple-50 dark:bg-purple-950">
                      AI Extracted: {summary.aiExtracted}
                    </Badge>
                  )}
                  {summary.aiResearched > 0 && (
                    <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-950">
                      AI Researched: {summary.aiResearched}
                    </Badge>
                  )}
                  {summary.manual > 0 && (
                    <Badge variant="outline" className="text-xs bg-orange-50 dark:bg-orange-950">
                      Manual: {summary.manual}
                    </Badge>
                  )}
                </div>
              )}

              {/* Grouped attributes table */}
              <div className="space-y-4">
                {['feed', 'ai_extracted', 'ai_researched', 'manual', 'unknown'].map((originKey) => {
                  const attrs = groupedAttributes[originKey];
                  if (!attrs || attrs.length === 0) return null;
                  const originInfo = originLabels[originKey];

                  return (
                    <div key={originKey} className="rounded-md border">
                      <div className={`px-3 py-2 ${originInfo.bg} border-b flex items-center gap-2`}>
                        <span className={`font-medium text-sm ${originInfo.color}`}>
                          {originInfo.label}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {attrs.length}
                        </Badge>
                      </div>
                      <div className="divide-y">
                        {attrs.map((attr) => (
                          <div key={attr.name} className="px-3 py-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-foreground">
                                  {attr.name.replace(/_/g, ' ')}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {formatValue(attr.value)}
                                  {attr.unit && !String(attr.value).toLowerCase().endsWith(attr.unit.toLowerCase()) && <span className="ml-1">{attr.unit}</span>}
                                </div>
                              </div>
                              {attr.confidence < 1 && (
                                <Badge variant="outline" className="text-xs ml-2">
                                  {Math.round(attr.confidence * 100)}%
                                </Badge>
                              )}
                            </div>
                            {/* Source information */}
                            {(attr.sourceText || attr.sourceUrl) && (
                              <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                                <span className="text-gray-400">Source:</span>
                                {attr.sourceUrl ? (
                                  <a
                                    href={attr.sourceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-700 hover:underline truncate max-w-[300px] inline-flex items-center gap-1"
                                    title={attr.sourceUrl}
                                  >
                                    {attr.sourceUrl.length > 50 ? attr.sourceUrl.substring(0, 50) + '...' : attr.sourceUrl}
                                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                  </a>
                                ) : (
                                  <span className="truncate max-w-[300px]" title={attr.sourceText || ''}>
                                    {attr.sourceText}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Overwritten value indicator */}
                            {attr.overwrittenFrom && (
                              <div className="mt-1.5 text-xs flex items-start gap-1.5 bg-amber-50 dark:bg-amber-950/30 px-2 py-1 rounded border border-amber-200 dark:border-amber-800">
                                <span className="text-amber-600 dark:text-amber-400">↳</span>
                                <div>
                                  <span className="text-amber-700 dark:text-amber-300">Overwrites: </span>
                                  <span className="line-through text-muted-foreground">{attr.overwrittenFrom.value}</span>
                                  <span className="text-muted-foreground ml-1">({attr.overwrittenFrom.source === 'feed' ? 'Feed' : attr.overwrittenFrom.source})</span>
                                  {attr.overwrittenFrom.reason && (
                                    <span className="text-amber-600 dark:text-amber-400 ml-1">- {attr.overwrittenFrom.reason}</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function ProductEnrichmentReview() {
  const { selectedSite } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState<SuspiciousProduct[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [pagination, setPagination] = useState<Pagination>({ total: 0, pageSize: 50, offset: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterInfo, setFilterInfo] = useState<FilterInfo | null>(null);

  // Initialize filters from URL params
  const [categoryFilter, setCategoryFilter] = useState(() => searchParams.get('category') || '');
  const [attributeTypeFilter, setAttributeTypeFilter] = useState<string>('');
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');

  // Batch selection state
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [batchActionLoading, setBatchActionLoading] = useState(false);

  // Dialog state for correcting values
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<SuspiciousProduct | null>(null);
  const [selectedAttribute, setSelectedAttribute] = useState<string>('');
  const [correctedValue, setCorrectedValue] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Detail view state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailProduct, setDetailProduct] = useState<SuspiciousProduct | null>(null);

  // AI suggestion state - keyed by productId-attributeName
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, AiSuggestion>>({});

  // Re-enrichment state
  const [reEnrichingProducts, setReEnrichingProducts] = useState<Set<string>>(new Set());
  const [batchJob, setBatchJob] = useState<{
    jobId: string;
    status: string;
    total: number;
    completed: number;
    succeeded: number;
    failed: number;
    progress: number;
    duration: string;
  } | null>(null);

  // Category gap mode state (from Domain Knowledge)
  const [categoryGapMode, setCategoryGapMode] = useState(false);
  const [categoryGapPath, setCategoryGapPath] = useState<string | null>(null);
  const [categoryGapAttribute, setCategoryGapAttribute] = useState<string | null>(null);
  const [categoryGapProducts, setCategoryGapProducts] = useState<Array<{
    id: string;
    name: string;
    brand: string;
    category: string;
    price: string;
    productUrl?: string;
    imageUrl?: string;
    missingAttribute: string;
  }>>([]);
  const [categoryGapLoading, setCategoryGapLoading] = useState(false);
  const [categoryGapTotal, setCategoryGapTotal] = useState(0);

  // Research mode state
  const [activeTab, setActiveTab] = useState<'review' | 'research' | 'opportunities' | 'validation' | 'feed-sync'>('opportunities');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<ProductSearchResult[]>([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedResearchProduct, setSelectedResearchProduct] = useState<ProductWithMissingSpecs | null>(null);
  const [researchProductLoading, setResearchProductLoading] = useState(false);
  const [highlightedSpec, setHighlightedSpec] = useState<string | null>(null);
  const [researchAiSuggestions, setResearchAiSuggestions] = useState<Record<string, AiSuggestion>>({});
  const [savingSpec, setSavingSpec] = useState<string | null>(null);
  const [pipelineRunId, setPipelineRunId] = useState<string | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Needs Research products (from AEO pipeline)
  const [needsResearchProducts, setNeedsResearchProducts] = useState<NeedsResearchProduct[]>([]);
  const [needsResearchLoading, setNeedsResearchLoading] = useState(false);
  const [needsResearchTotal, setNeedsResearchTotal] = useState(0);

  // AEO regeneration state
  const [regeneratingAeo, setRegeneratingAeo] = useState(false);

  // Research mode correction dialog state
  const [researchCorrectDialogOpen, setResearchCorrectDialogOpen] = useState(false);
  const [researchCorrectSpecName, setResearchCorrectSpecName] = useState('');
  const [researchCorrectValue, setResearchCorrectValue] = useState('');
  const [researchCorrectIsExisting, setResearchCorrectIsExisting] = useState(false);
  const [researchCorrectCurrentValue, setResearchCorrectCurrentValue] = useState('');

  // Existing spec verification state
  const [expandedExistingSpec, setExpandedExistingSpec] = useState<string | null>(null);
  const [existingSpecAiResults, setExistingSpecAiResults] = useState<Record<string, AiSuggestion>>({});

  // Export dialog state (Story 358)
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Accepted values management dialog state
  const [acceptedValuesDialogOpen, setAcceptedValuesDialogOpen] = useState(false);
  const [siteAttributeValues, setSiteAttributeValues] = useState<SiteAttributeValue[]>([]);
  const [acceptedValuesLoading, setAcceptedValuesLoading] = useState(false);
  const [deletingValue, setDeletingValue] = useState<string | null>(null);

  const fetchSuspiciousProducts = useCallback(async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        siteId: selectedSite.id || selectedSite._id,
        pageSize: pageSize.toString(),
        offset: pagination.offset.toString(),
      });

      if (categoryFilter) {
        params.append('categoryPath', categoryFilter);
      }

      if (attributeTypeFilter) {
        params.append('attributeType', attributeTypeFilter);
      }

      const response = await authFetch(`${API_URL}/product-enrichment/suspicious?${params}`);
      const data = await response.json();

      if (data.success) {
        setProducts(data.data.products);
        setStats(data.data.stats);
        setPagination(data.data.pagination);
        setFilterInfo(data.data.filters);
        // Clear selection when products change
        setSelectedProducts(new Set());
      } else {
        setError(data.error || 'Failed to fetch suspicious products');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedSite, pageSize, pagination.offset, categoryFilter, attributeTypeFilter]);

  useEffect(() => {
    fetchSuspiciousProducts();
  }, [fetchSuspiciousProducts]);

  // Sync category filter to URL
  useEffect(() => {
    const currentCategory = searchParams.get('category') || '';
    if (categoryFilter !== currentCategory) {
      if (categoryFilter) {
        setSearchParams({ category: categoryFilter }, { replace: true });
      } else {
        searchParams.delete('category');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [categoryFilter, searchParams, setSearchParams]);

  // Handle URL params for deep links from Pipeline or Domain Knowledge
  useEffect(() => {
    const mode = searchParams.get('mode');
    const productId = searchParams.get('productId');
    const spec = searchParams.get('spec');
    const runId = searchParams.get('runId');
    const categoryPath = searchParams.get('categoryPath');
    const missingAttribute = searchParams.get('missingAttribute');

    if (mode === 'research') {
      setActiveTab('research');
      if (runId) {
        setPipelineRunId(runId);
      }
      if (productId) {
        // Load the product's missing specs
        loadProductMissingSpecs(productId);
        if (spec) {
          setHighlightedSpec(spec);
        }
      }
    }

    // Category gap mode - from Domain Knowledge link
    if (categoryPath) {
      setCategoryGapMode(true);
      setCategoryGapPath(categoryPath);
      setCategoryGapAttribute(missingAttribute || null);
      setActiveTab('research');
    }
  }, [searchParams]);

  // Load category gap products when in category gap mode
  useEffect(() => {
    if (!categoryGapMode || !selectedSite || !categoryGapPath) return;

    const loadCategoryGapProducts = async () => {
      setCategoryGapLoading(true);
      try {
        const params = new URLSearchParams({
          siteId: selectedSite.id || selectedSite._id,
          categoryPath: categoryGapPath,
        });
        if (categoryGapAttribute) {
          params.append('attribute', categoryGapAttribute);
        }

        const response = await authFetch(`${API_URL}/product-enrichment/missing-by-category?${params}`);
        const data = await response.json();

        if (data.success) {
          setCategoryGapProducts(data.data.products);
          setCategoryGapTotal(data.data.context.totalMissing);
        }
      } catch (err) {
        console.error('Error loading category gap products:', err);
      } finally {
        setCategoryGapLoading(false);
      }
    };

    loadCategoryGapProducts();
  }, [categoryGapMode, selectedSite, categoryGapPath, categoryGapAttribute]);

  // Load products needing research - extracted for reuse after regeneration
  const loadNeedsResearchProducts = useCallback(async () => {
    if (!selectedSite) return;

    setNeedsResearchLoading(true);
    try {
      const params = new URLSearchParams({
        siteId: selectedSite.id || selectedSite._id,
        limit: '12',
      });

      const response = await authFetch(`${API_URL}/product-enrichment/needs-research?${params}`);
      const data = await response.json();

      if (data.success) {
        setNeedsResearchProducts(data.data.products);
        setNeedsResearchTotal(data.data.total);
      }
    } catch (err) {
      console.error('Error loading needs-research products:', err);
    } finally {
      setNeedsResearchLoading(false);
    }
  }, [selectedSite]);

  // Load products needing research when Research tab is active
  useEffect(() => {
    if (activeTab !== 'research' || !selectedSite) return;
    loadNeedsResearchProducts();
  }, [activeTab, selectedSite, loadNeedsResearchProducts]);

  // Product search with debounce
  const searchProducts = useCallback(async (query: string) => {
    if (!selectedSite || query.length < 2) {
      setProductSearchResults([]);
      return;
    }

    setProductSearchLoading(true);
    try {
      const params = new URLSearchParams({
        siteId: selectedSite.id || selectedSite._id,
        q: query,
        limit: '10',
      });
      const response = await authFetch(`${API_URL}/product-enrichment/search?${params}`);
      const data = await response.json();
      if (data.success) {
        setProductSearchResults(data.products);
      }
    } catch (err) {
      console.error('Error searching products:', err);
    } finally {
      setProductSearchLoading(false);
    }
  }, [selectedSite]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (productSearchQuery.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchProducts(productSearchQuery);
      }, 300);
    } else {
      setProductSearchResults([]);
    }
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [productSearchQuery, searchProducts]);

  // Load missing specs for a product
  const loadProductMissingSpecs = async (productId: string) => {
    setResearchProductLoading(true);
    setSelectedResearchProduct(null);
    setResearchAiSuggestions({});
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/missing-specs/${productId}`);
      const data = await response.json();
      if (data.success) {
        setSelectedResearchProduct(data);
      } else {
        setError(data.error || 'Failed to load product specifications');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      setResearchProductLoading(false);
    }
  };

  // Handle selecting a product from search
  const handleSelectProduct = (product: ProductSearchResult) => {
    setProductSearchQuery('');
    setProductSearchResults([]);
    loadProductMissingSpecs(product.id);
  };

  // Ask AI for a missing spec
  const handleResearchAskAI = async (spec: MissingSpec) => {
    if (!selectedResearchProduct) return;
    const key = spec.spec;

    setResearchAiSuggestions(prev => ({
      ...prev,
      [key]: { attributeName: spec.spec, value: '', loading: true }
    }));

    try {
      const response = await authFetch(`${API_URL}/product-enrichment/ask-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productName: selectedResearchProduct.productName,
          brand: selectedResearchProduct.brand,
          category: selectedResearchProduct.category,
          attributeName: spec.spec,
          sourceSiteUrl: selectedSite?.url, // Exclude this site from web search
        }),
      });

      const data = await response.json();

      if (data.success && data.found) {
        setResearchAiSuggestions(prev => ({
          ...prev,
          [key]: {
            attributeName: spec.spec,
            value: data.value,
            loading: false,
            model: data.model,
            source: data.source,
            sourceTitle: data.sourceTitle,
            searchedWeb: data.searchedWeb,
            numericValue: data.numericValue,
            unit: data.unit,
          }
        }));
      } else {
        setResearchAiSuggestions(prev => ({
          ...prev,
          [key]: { attributeName: spec.spec, value: '', loading: false, searchedWeb: data.searchedWeb }
        }));
        setError(data.message || 'AI could not determine this value');
      }
    } catch (err) {
      setResearchAiSuggestions(prev => ({
        ...prev,
        [key]: { attributeName: spec.spec, value: '', loading: false }
      }));
      setError(err instanceof Error ? err.message : 'Failed to ask AI');
    }
  };

  // Save a researched spec
  const handleSaveSpec = async (specName: string, suggestion: AiSuggestion) => {
    if (!selectedResearchProduct) return;

    setSavingSpec(specName);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/save-spec/${selectedResearchProduct.productId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          specName,
          value: suggestion.value,
          numericValue: suggestion.numericValue,
          unit: suggestion.unit,
          source: suggestion.source,
          researchedVia: 'ask_ai',
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Reload the product to see updated specs
        await loadProductMissingSpecs(selectedResearchProduct.productId);
        // Clear the suggestion
        setResearchAiSuggestions(prev => {
          const { [specName]: _, ...rest } = prev;
          return rest;
        });
      } else {
        setError(data.error || 'Failed to save specification');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save specification');
    } finally {
      setSavingSpec(null);
    }
  };

  // Open correction dialog for research mode (both missing and existing specs)
  const openResearchCorrectDialog = (specName: string, isExisting: boolean, currentValue?: string) => {
    setResearchCorrectSpecName(specName);
    setResearchCorrectIsExisting(isExisting);
    setResearchCorrectCurrentValue(currentValue || '');
    setResearchCorrectValue(currentValue || '');
    setResearchCorrectDialogOpen(true);
  };

  // Handle saving a manually corrected value in research mode
  const handleResearchCorrect = async () => {
    if (!selectedResearchProduct || !researchCorrectSpecName || !researchCorrectValue) return;

    setSavingSpec(researchCorrectSpecName);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/save-spec/${selectedResearchProduct.productId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          specName: researchCorrectSpecName,
          value: researchCorrectValue,
          source: 'manual_correction',
          researchedVia: 'manual',
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Reload the product to see updated specs
        await loadProductMissingSpecs(selectedResearchProduct.productId);
        // Clear the dialog and any AI suggestions for this spec
        setResearchCorrectDialogOpen(false);
        setResearchCorrectSpecName('');
        setResearchCorrectValue('');
        setResearchAiSuggestions(prev => {
          const { [researchCorrectSpecName]: _, ...rest } = prev;
          return rest;
        });
        setExistingSpecAiResults(prev => {
          const { [researchCorrectSpecName]: _, ...rest } = prev;
          return rest;
        });
        setExpandedExistingSpec(null);
      } else {
        setError(data.error || 'Failed to save correction');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save correction');
    } finally {
      setSavingSpec(null);
    }
  };

  // Handle marking a spec as N/A (unavailable) in research mode
  const handleResearchMarkUnavailable = async (specName: string) => {
    if (!selectedResearchProduct) return;

    setSavingSpec(specName);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/save-spec/${selectedResearchProduct.productId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          specName,
          value: 'N/A',
          source: 'marked_unavailable',
          researchedVia: 'manual',
          isUnavailable: true,
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Reload the product to see updated specs
        await loadProductMissingSpecs(selectedResearchProduct.productId);
        // Clear any AI suggestions
        setResearchAiSuggestions(prev => {
          const { [specName]: _, ...rest } = prev;
          return rest;
        });
        setExistingSpecAiResults(prev => {
          const { [specName]: _, ...rest } = prev;
          return rest;
        });
        setExpandedExistingSpec(null);
      } else {
        setError(data.error || 'Failed to mark as unavailable');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark as unavailable');
    } finally {
      setSavingSpec(null);
    }
  };

  // Verify an existing spec value with AI
  const handleVerifyExistingSpec = async (specName: string, currentValue: string) => {
    if (!selectedResearchProduct) return;

    setExistingSpecAiResults(prev => ({
      ...prev,
      [specName]: { attributeName: specName, value: '', loading: true }
    }));

    try {
      const response = await authFetch(`${API_URL}/product-enrichment/ask-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productName: selectedResearchProduct.productName,
          brand: selectedResearchProduct.brand,
          category: selectedResearchProduct.category,
          attributeName: specName,
          sourceSiteUrl: selectedSite?.url,
        }),
      });

      const data = await response.json();

      if (data.success && data.found) {
        setExistingSpecAiResults(prev => ({
          ...prev,
          [specName]: {
            attributeName: specName,
            value: data.value,
            loading: false,
            model: data.model,
            source: data.source,
            sourceTitle: data.sourceTitle,
            searchedWeb: data.searchedWeb,
            numericValue: data.numericValue,
            unit: data.unit,
          }
        }));
      } else {
        setExistingSpecAiResults(prev => ({
          ...prev,
          [specName]: { attributeName: specName, value: '', loading: false, searchedWeb: data.searchedWeb }
        }));
      }
    } catch (err) {
      setExistingSpecAiResults(prev => ({
        ...prev,
        [specName]: { attributeName: specName, value: '', loading: false }
      }));
      setError(err instanceof Error ? err.message : 'Failed to verify spec');
    }
  };

  // Update existing spec with AI-found value
  const handleUpdateExistingSpec = async (specName: string, newValue: string, numericValue?: number | null, unit?: string | null) => {
    if (!selectedResearchProduct) return;

    setSavingSpec(specName);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/save-spec/${selectedResearchProduct.productId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          specName,
          value: newValue,
          numericValue,
          unit,
          source: 'ai_verification',
          researchedVia: 'ask_ai',
        }),
      });

      const data = await response.json();
      if (data.success) {
        await loadProductMissingSpecs(selectedResearchProduct.productId);
        setExistingSpecAiResults(prev => {
          const { [specName]: _, ...rest } = prev;
          return rest;
        });
        setExpandedExistingSpec(null);
      } else {
        setError(data.error || 'Failed to update specification');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update specification');
    } finally {
      setSavingSpec(null);
    }
  };

  // Get severity badge color
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return <Badge variant="destructive">Critical</Badge>;
      case 'HIGH':
        return <Badge className="bg-orange-500 hover:bg-orange-600">High</Badge>;
      case 'MEDIUM':
        return <Badge variant="secondary">Medium</Badge>;
      case 'LOW':
        return <Badge variant="outline">Low</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const handleApprove = async (
    productId: string,
    attributeName: string,
    newValue?: string,
    numericValue?: number | null,
    unit?: string | null
  ) => {
    setActionLoading(true);
    try {
      const body: Record<string, unknown> = { attributeName, action: newValue ? 'correct' : 'approve' };
      if (newValue) {
        body.correctedValue = newValue;
        body.numericValue = numericValue;
        body.unit = unit;
      }

      const response = await authFetch(`${API_URL}/product-enrichment/resolve-suspicious/${productId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data.success) {
        // Clear AI suggestion for this attribute
        const key = `${productId}-${attributeName}`;
        setAiSuggestions(prev => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });
        // Refresh the list
        fetchSuspiciousProducts();
      } else {
        setError(data.error || 'Failed to approve attribute');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCorrect = async () => {
    if (!selectedProduct || !selectedAttribute) return;

    setActionLoading(true);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/resolve-suspicious/${selectedProduct.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributeName: selectedAttribute,
          action: 'correct',
          correctedValue: correctedValue,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setEditDialogOpen(false);
        setCorrectedValue('');
        setSelectedProduct(null);
        setSelectedAttribute('');
        fetchSuspiciousProducts();
      } else {
        setError(data.error || 'Failed to correct attribute');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to correct');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkUnavailable = async (productId: string, attributeName: string, reason?: string) => {
    setActionLoading(true);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/resolve-suspicious/${productId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributeName,
          action: 'mark_unavailable',
          reason: reason || 'Value cannot be reliably determined from available sources',
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Clear AI suggestion for this attribute
        const key = `${productId}-${attributeName}`;
        setAiSuggestions(prev => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });
        // Refresh the list
        fetchSuspiciousProducts();
      } else {
        setError(data.error || 'Failed to mark as unavailable');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark as unavailable');
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk replace a value across all products in the site
  const handleBulkReplace = async (
    productId: string,
    attributeName: string,
    oldValue: string,
    newValue: string
  ) => {
    if (!selectedSite?.id) return;

    setActionLoading(true);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/bulk-replace-value`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributeName,
          oldValue,
          newValue,
          siteId: selectedSite.id,
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Clear AI suggestion
        const key = `${productId}-${attributeName}`;
        setAiSuggestions(prev => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });
        // Refresh the list
        fetchSuspiciousProducts();
        setError(null);
        alert(`Successfully updated ${data.updatedCount} products.`);
      } else {
        setError(data.error || 'Failed to bulk replace value');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk replace');
    } finally {
      setActionLoading(false);
    }
  };

  // Accept a non-standard value by adding it to canonical enum_values
  const handleAcceptEnumValue = async (
    productId: string,
    attributeName: string,
    value: string,
    category: string
  ) => {
    if (!selectedSite) return;
    setActionLoading(true);
    try {
      const response = await authFetch(`${API_URL}/product-enrichment/accept-enum-value`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributeName,
          value,
          category,
          siteId: selectedSite.id || selectedSite._id,
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Clear AI suggestion
        const key = `${productId}-${attributeName}`;
        setAiSuggestions(prev => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });
        // Refresh the list
        fetchSuspiciousProducts();
        setError(null);
        alert(data.alreadyExists
          ? `"${value}" is already in the allowed values.`
          : `Added "${value}" to allowed values for ${attributeName}.`);
      } else {
        setError(data.error || 'Failed to accept enum value');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept enum value');
    } finally {
      setActionLoading(false);
    }
  };

  // Fetch site-level accepted values for the management dialog
  const fetchSiteAttributeValues = async () => {
    if (!selectedSite) return;
    setAcceptedValuesLoading(true);
    try {
      const response = await authFetch(
        `${API_URL}/product-enrichment/site-attribute-values?siteId=${selectedSite.id || selectedSite._id}`
      );
      const data = await response.json();
      if (data.success) {
        setSiteAttributeValues(data.values || []);
      }
    } catch (err) {
      console.error('Failed to fetch site attribute values:', err);
    } finally {
      setAcceptedValuesLoading(false);
    }
  };

  // Remove an accepted value from the site-level overrides
  const handleRemoveAcceptedValue = async (recordId: number, value: string, attributeName: string) => {
    if (!confirm(`Remove "${value}" from accepted values for ${attributeName}?\n\nProducts with this value will be re-flagged for review.`)) {
      return;
    }

    setDeletingValue(`${recordId}-${value}`);
    try {
      const response = await authFetch(
        `${API_URL}/product-enrichment/site-attribute-values/${recordId}/value`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value, reflagProducts: true }),
        }
      );
      const data = await response.json();
      if (data.success) {
        // Refresh the accepted values list
        fetchSiteAttributeValues();
        // Refresh the suspicious products list to show re-flagged products
        fetchSuspiciousProducts();
      } else {
        setError(data.error || 'Failed to remove value');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove value');
    } finally {
      setDeletingValue(null);
    }
  };

  // Open the accepted values dialog and fetch data
  const openAcceptedValuesDialog = () => {
    setAcceptedValuesDialogOpen(true);
    fetchSiteAttributeValues();
  };

  const handleAskAI = async (product: SuspiciousProduct, attrName: string) => {
    const key = `${product.id}-${attrName}`;

    // Set loading state
    setAiSuggestions(prev => ({
      ...prev,
      [key]: { attributeName: attrName, value: '', loading: true }
    }));

    try {
      const response = await authFetch(`${API_URL}/product-enrichment/ask-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productName: product.name,
          brand: product.brand,
          category: product.category,
          attributeName: attrName,
          description: product.description,
          sourceSiteUrl: selectedSite?.url, // Exclude this site from web search
          siteId: selectedSite?.id || selectedSite?._id, // For site-level value overrides
        }),
      });

      const data = await response.json();

      if (data.success && data.found) {
        setAiSuggestions(prev => ({
          ...prev,
          [key]: {
            attributeName: attrName,
            value: data.value,
            loading: false,
            model: data.model,
            source: data.source,
            sourceTitle: data.sourceTitle,
            searchedWeb: data.searchedWeb,
            numericValue: data.numericValue,
            unit: data.unit,
            similarityMatches: data.similarityMatches,
            totalWithValue: data.totalWithValue,
          }
        }));
      } else {
        setAiSuggestions(prev => ({
          ...prev,
          [key]: { attributeName: attrName, value: '', loading: false, searchedWeb: data.searchedWeb }
        }));
        setError(data.message || 'AI could not determine this value');
      }
    } catch (err) {
      setAiSuggestions(prev => ({
        ...prev,
        [key]: { attributeName: attrName, value: '', loading: false }
      }));
      setError(err instanceof Error ? err.message : 'Failed to ask AI');
    }
  };

  const handleReEnrich = async (productId: string) => {
    setReEnrichingProducts(prev => new Set(prev).add(productId));
    setError(null);

    try {
      const response = await authFetch(`${API_URL}/product-enrichment/enrich/${productId}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        // Refresh the list to see updated data
        await fetchSuspiciousProducts();
      } else {
        setError(data.error || 'Failed to re-enrich product');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to re-enrich');
    } finally {
      setReEnrichingProducts(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  // Regenerate AEO for a product (used when specs are complete but AEO is needs_research)
  const handleRegenerateAeo = async (aeoGenerationId: string) => {
    setRegeneratingAeo(true);
    setError(null);

    try {
      const response = await authFetch(`${API_URL}/content-review/product_aeo/${aeoGenerationId}/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to regenerate AEO');
        return;
      }

      // Refresh the product data to show updated status (now 'draft' instead of 'needs_research')
      if (selectedResearchProduct) {
        await loadProductMissingSpecs(selectedResearchProduct.productId);
      }

      // Refresh the needs-research list since this product is no longer needs_research
      await loadNeedsResearchProducts();

      // Show success message - the status should now be 'draft'
      alert('AEO regenerated successfully! The content is now in draft status and ready for review.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate AEO');
    } finally {
      setRegeneratingAeo(false);
    }
  };

  const handleReEnrichAll = async () => {
    if (filteredProducts.length === 0) return;

    setError(null);

    try {
      // Start batch job
      const response = await authFetch(`${API_URL}/product-enrichment/batch-enrich`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productIds: filteredProducts.map(p => p.id),
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to start batch enrichment');
        return;
      }

      // Initialize job state and start polling
      setBatchJob({
        jobId: data.jobId,
        status: 'running',
        total: filteredProducts.length,
        completed: 0,
        succeeded: 0,
        failed: 0,
        progress: 0,
        duration: '0',
      });

      // Poll for progress
      pollBatchJobStatus(data.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start batch enrichment');
    }
  };

  const pollBatchJobStatus = async (jobId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await authFetch(`${API_URL}/product-enrichment/batch-enrich/${jobId}`);
        const data = await response.json();

        if (!data.success) {
          clearInterval(pollInterval);
          setBatchJob(null);
          setError(data.error || 'Failed to get job status');
          return;
        }

        const job = data.job;
        setBatchJob({
          jobId: job.id,
          status: job.status,
          total: job.total,
          completed: job.completed,
          succeeded: job.succeeded,
          failed: job.failed,
          progress: job.progress,
          duration: job.duration,
        });

        // Job completed
        if (job.status === 'completed') {
          clearInterval(pollInterval);

          // Show summary
          if (job.failed > 0) {
            setError(`Re-enriched ${job.succeeded} products, ${job.failed} failed`);
          }

          // Clear job state after a moment and refresh
          setTimeout(() => {
            setBatchJob(null);
            fetchSuspiciousProducts();
          }, 2000);
        }
      } catch (err) {
        console.error('Error polling batch job:', err);
        // Don't clear interval on network errors, retry
      }
    }, 2000); // Poll every 2 seconds
  };

  const openCorrectDialog = (product: SuspiciousProduct, attrName: string) => {
    setSelectedProduct(product);
    setSelectedAttribute(attrName);
    const attr = product.suspicious_attributes[attrName];
    setCorrectedValue(typeof attr?.value === 'object' ? JSON.stringify(attr.value) : String(attr?.value || ''));
    setEditDialogOpen(true);
  };

  const openDetailDialog = (product: SuspiciousProduct) => {
    setDetailProduct(product);
    setDetailDialogOpen(true);
  };

  // Batch selection helpers
  const toggleProductSelection = (productId: string) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const selectAllProducts = () => {
    setSelectedProducts(new Set(products.map(p => p.id)));
  };

  const clearSelection = () => {
    setSelectedProducts(new Set());
  };

  // Batch resolve function
  const handleBatchResolve = async (action: 'approve' | 'mark_unavailable') => {
    if (selectedProducts.size === 0) return;

    setBatchActionLoading(true);
    setError(null);

    try {
      // Build resolutions for all selected products and their suspicious attributes
      const resolutions: Array<{
        productId: string;
        attributeName: string;
        action: string;
        reason?: string;
      }> = [];

      for (const productId of selectedProducts) {
        const product = products.find(p => p.id === productId);
        if (!product?.suspicious_attributes) continue;

        for (const attrName of Object.keys(product.suspicious_attributes)) {
          resolutions.push({
            productId,
            attributeName: attrName,
            action,
            ...(action === 'mark_unavailable' && { reason: 'Batch marked unavailable' }),
          });
        }
      }

      if (resolutions.length === 0) {
        setError('No attributes to resolve');
        return;
      }

      const response = await authFetch(`${API_URL}/product-enrichment/batch-resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolutions }),
      });

      const data = await response.json();

      if (data.success || response.status === 207) {
        // Refresh the list
        await fetchSuspiciousProducts();

        if (data.data?.failed > 0) {
          setError(`Processed ${data.data.processed}, ${data.data.failed} failed`);
        }
      } else {
        setError(data.error || 'Batch operation failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Batch operation failed');
    } finally {
      setBatchActionLoading(false);
    }
  };

  const formatAttributeValue = (attr: SuspiciousAttribute): string => {
    if (typeof attr.value === 'object') {
      return JSON.stringify(attr.value);
    }
    let result = String(attr.value);
    // Only append unit if value doesn't already end with it (avoid "240g g")
    if (attr.unit && !result.toLowerCase().endsWith(attr.unit.toLowerCase())) {
      result += ` ${attr.unit}`;
    }
    return result;
  };

  const getConfidenceBadge = (confidence?: number) => {
    if (confidence === undefined) return null;
    const color = confidence >= 0.8
      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
      : confidence >= 0.6
        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    return (
      <Badge variant="outline" className={color}>
        {(confidence * 100).toFixed(0)}% confidence
      </Badge>
    );
  };

  const filteredProducts = searchTerm
    ? products.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.brand.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : products;

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select a site from the header to review enrichment data.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            {activeTab === 'review' ? (
              <AlertTriangle className="h-6 w-6 text-amber-500" />
            ) : (
              <FileSearch className="h-6 w-6 text-blue-500" />
            )}
            Enrichment Review
          </h1>
          <p className="text-muted-foreground mt-1">
            {activeTab === 'review'
              ? 'Review and correct suspicious AI-enriched product attributes'
              : 'Research missing product specifications using AI'}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {activeTab === 'review' && batchJob && (
            <div className="flex items-center gap-3 text-sm bg-purple-50 dark:bg-purple-950/30 px-3 py-2 rounded-lg border border-purple-200 dark:border-purple-800">
              <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
              <div className="flex flex-col">
                <span className="text-purple-700 dark:text-purple-300 font-medium">
                  {batchJob.status === 'completed' ? 'Completed!' : `Processing ${batchJob.completed}/${batchJob.total}`}
                </span>
                <span className="text-xs text-purple-600 dark:text-purple-400">
                  {batchJob.succeeded} succeeded, {batchJob.failed} failed • {batchJob.duration}s
                </span>
              </div>
              <div className="w-20 h-2 bg-purple-200 dark:bg-purple-900 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-600 transition-all duration-300"
                  style={{ width: `${batchJob.progress}%` }}
                />
              </div>
            </div>
          )}
          {activeTab === 'review' && (
            <>
              <Button
                onClick={handleReEnrichAll}
                variant="default"
                disabled={loading || filteredProducts.length === 0 || !!batchJob}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Zap className="h-4 w-4 mr-2" />
                Re-enrich All ({filteredProducts.length})
              </Button>
              <Button onClick={fetchSuspiciousProducts} variant="outline" disabled={loading || !!batchJob}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={openAcceptedValuesDialog} variant="outline" title="Manage site-level accepted values">
                <Settings className="h-4 w-4 mr-2" />
                Accepted Values
              </Button>
            </>
          )}
          {activeTab === 'opportunities' && (
            <Button variant="outline" onClick={() => setShowExportDialog(true)}>
              <Download className="h-4 w-4 mr-2" />
              Export Missing Fields
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'review' | 'research' | 'opportunities' | 'validation' | 'feed-sync')} className="space-y-4">
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          <TabsTrigger value="opportunities" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Spec Gaps
          </TabsTrigger>
          <TabsTrigger value="review" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Review Suspicious
          </TabsTrigger>
          <TabsTrigger value="research" className="flex items-center gap-2">
            <FileSearch className="h-4 w-4" />
            Research Product
          </TabsTrigger>
          <TabsTrigger value="validation" className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Validation Tools
          </TabsTrigger>
          <TabsTrigger value="feed-sync" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Feed Sync
          </TabsTrigger>
        </TabsList>

        {/* Spec Gaps Tab Content */}
        <TabsContent value="opportunities" className="space-y-6">
          {selectedSite && (
            <ContentOpportunities
              siteId={selectedSite.id}
              onResearchClick={(category, attribute) => {
                // Switch to Research Product tab with category gap filter
                setCategoryGapMode(true);
                setCategoryGapPath(category);
                setCategoryGapAttribute(attribute || null);
                setActiveTab('research');
              }}
            />
          )}
        </TabsContent>

        {/* Review Tab Content */}
        <TabsContent value="review" className="space-y-6">
          {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Enriched Products
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_enriched_products.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-amber-700 dark:text-amber-400">
                Needs Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                {stats.products_needing_review.toLocaleString()}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Review Percentage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.review_percentage}%</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col gap-3">
            {/* Active filters indicator */}
            {(categoryFilter || attributeTypeFilter) && (
              <div className="flex items-center gap-2 flex-wrap">
                {categoryFilter && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    <Filter className="h-3 w-3 mr-1" />
                    Category: {categoryFilter}
                  </Badge>
                )}
                {attributeTypeFilter && (
                  <Badge variant="secondary" className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Error Type: {attributeTypeFilter}
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCategoryFilter('');
                    setAttributeTypeFilter('');
                  }}
                  className="h-6 px-2 text-muted-foreground hover:text-foreground"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              </div>
            )}

            {/* Batch Selection Bar */}
            {selectedProducts.size > 0 && (
              <div className="flex items-center gap-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  {selectedProducts.size} product{selectedProducts.size > 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => handleBatchResolve('approve')}
                    disabled={batchActionLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {batchActionLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                    )}
                    Approve All
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleBatchResolve('mark_unavailable')}
                    disabled={batchActionLoading}
                  >
                    <Ban className="h-4 w-4 mr-1" />
                    Mark N/A
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearSelection}
                    disabled={batchActionLoading}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by product name or brand..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="w-48">
                <div className="relative">
                  <Filter className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Filter by category..."
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="w-48">
                <Select
                  value={attributeTypeFilter}
                  onValueChange={(value) => setAttributeTypeFilter(value === 'all' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by error type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Error Types</SelectItem>
                    {filterInfo?.availableAttributeTypes.map(({ name, count }) => (
                      <SelectItem key={name} value={name}>
                        {name.replace(/_/g, ' ')} ({count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value));
                    setPagination(prev => ({ ...prev, offset: 0 }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Page size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20 / page</SelectItem>
                    <SelectItem value="50">50 / page</SelectItem>
                    <SelectItem value="100">100 / page</SelectItem>
                    <SelectItem value="0">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {products.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectedProducts.size === products.length ? clearSelection : selectAllProducts}
                  className="self-center"
                >
                  {selectedProducts.size === products.length ? (
                    <>
                      <Square className="h-4 w-4 mr-1" />
                      Deselect All
                    </>
                  ) : (
                    <>
                      <CheckSquare className="h-4 w-4 mr-1" />
                      Select All ({products.length})
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Products List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <p className="text-lg font-medium">No suspicious attributes found!</p>
            <p className="text-sm mt-1">All enriched products have valid attribute values.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredProducts.map((product) => (
            <Card
              key={product.id}
              className={`border-l-4 ${selectedProducts.has(product.id)
                ? 'border-l-blue-500 dark:border-l-blue-400 bg-blue-50/50 dark:bg-blue-950/20'
                : 'border-l-amber-400 dark:border-l-amber-500'
              }`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedProducts.has(product.id)}
                      onCheckedChange={() => toggleProductSelection(product.id)}
                      className="mt-1"
                    />
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        {product.product_url ? (
                          <a
                            href={cleanProductUrl(product.product_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline hover:text-primary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {product.name}
                            <ExternalLink className="h-3 w-3 ml-1 inline-block opacity-50" />
                          </a>
                        ) : (
                          product.name
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        <span className="font-medium">{product.brand}</span> | {product.category}
                        {product.price && ` | ${product.price}`}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {product.product_url && (
                      <a
                        href={cleanProductUrl(product.product_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex"
                      >
                        <Button variant="outline" size="sm">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View Product
                        </Button>
                      </a>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReEnrich(product.id)}
                      disabled={reEnrichingProducts.has(product.id)}
                      className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                      title="Re-run AI enrichment with improved prompts"
                    >
                      {reEnrichingProducts.has(product.id) ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4 mr-1" />
                      )}
                      Re-enrich
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setActiveTab('research');
                        loadProductMissingSpecs(product.id);
                      }}
                      className="border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950"
                      title="Open in Research Product tab"
                    >
                      <FileSearch className="h-4 w-4 mr-1" />
                      Research
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openDetailDialog(product)}>
                      <Eye className="h-4 w-4 mr-1" />
                      View All
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(product.suspicious_attributes || {}).map(([attrName, attr]) => {
                    const aiKey = `${product.id}-${attrName}`;
                    const aiSuggestion = aiSuggestions[aiKey];

                    return (
                      <div
                        key={attrName}
                        className="flex items-start justify-between p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-amber-900 dark:text-amber-200">{attrName}</span>
                            {getConfidenceBadge(attr.confidence)}
                          </div>
                          <div className="text-sm">
                            <span className="text-amber-800 dark:text-amber-300">Value: </span>
                            <span className="font-mono bg-white dark:bg-slate-800 px-2 py-0.5 rounded text-foreground">
                              {formatAttributeValue(attr)}
                            </span>
                          </div>
                          {attr.source && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Source: "{attr.source}"
                            </div>
                          )}
                          {attr.validation_note && (
                            <div className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {attr.validation_note}
                            </div>
                          )}

                          {/* AI Suggestion */}
                          {aiSuggestion?.value && (
                            <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded">
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                  <span className="text-blue-800 dark:text-blue-300">
                                    AI Suggestion{aiSuggestion.searchedWeb ? ' (via Web Search)' : ''}:
                                  </span>
                                  <span className="font-mono font-medium flex-1">{aiSuggestion.value}</span>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handleApprove(
                                      product.id,
                                      attrName,
                                      aiSuggestion.value,
                                      aiSuggestion.numericValue,
                                      aiSuggestion.unit
                                    )}
                                    disabled={actionLoading}
                                    className="bg-blue-600 hover:bg-blue-700"
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-1" />
                                    Use This
                                  </Button>
                                </div>
                                {/* Show source when web search was used */}
                                {aiSuggestion.searchedWeb && aiSuggestion.source && (
                                  <div className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                                    <Search className="h-3 w-3" />
                                    Source:
                                    {aiSuggestion.source.startsWith('http') ? (
                                      <a
                                        href={aiSuggestion.source.replace(/\?utm_source=openai$/, '')}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline flex items-center gap-1"
                                      >
                                        {aiSuggestion.sourceTitle || 'Web Search Result'}
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    ) : (
                                      <span>{aiSuggestion.source}</span>
                                    )}
                                  </div>
                                )}

                                {/* Similarity Suggestions - when value might not match canonical enum */}
                                {aiSuggestion.similarityMatches && aiSuggestion.similarityMatches.length > 0 && (
                                  <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded">
                                    <div className="text-xs text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Value "{aiSuggestion.value}" is not in allowed values. Did you mean:
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {aiSuggestion.similarityMatches.map((match, idx) => (
                                        <div key={idx} className="flex items-center gap-1">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleApprove(
                                              product.id,
                                              attrName,
                                              match.suggestedValue,
                                              null,
                                              null
                                            )}
                                            disabled={actionLoading}
                                            className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                                          >
                                            Replace by "{match.suggestedValue}"
                                          </Button>
                                          {(aiSuggestion.totalWithValue ?? 0) > 1 && idx === 0 && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => {
                                                if (confirm(`Replace "${aiSuggestion.value}" with "${match.suggestedValue}" in ${aiSuggestion.totalWithValue} products?`)) {
                                                  handleBulkReplace(
                                                    product.id,
                                                    attrName,
                                                    aiSuggestion.value,
                                                    match.suggestedValue
                                                  );
                                                }
                                              }}
                                              disabled={actionLoading}
                                              className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                                            >
                                              Replace All ({aiSuggestion.totalWithValue})
                                            </Button>
                                          )}
                                        </div>
                                      ))}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          if (confirm(`Accept "${aiSuggestion.value}" as a valid value for ${attrName}? This will add it to the allowed values list.`)) {
                                            handleAcceptEnumValue(
                                              product.id,
                                              attrName,
                                              aiSuggestion.value,
                                              product.category
                                            );
                                          }
                                        }}
                                        disabled={actionLoading}
                                        className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                                      >
                                        Accept "{aiSuggestion.value}" as valid
                                        {(aiSuggestion.totalWithValue ?? 0) > 1 ? ` (${aiSuggestion.totalWithValue})` : ''}
                                      </Button>
                                    </div>
                                  </div>
                                )}

                                {/* Show Accept as valid when value is not in allowed values but no similarity match found */}
                                {aiSuggestion.similarityMatches && aiSuggestion.similarityMatches.length === 0 &&
                                 attr.validation_note?.includes('not in allowed values') && (
                                  <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded">
                                    <div className="text-xs text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Value "{aiSuggestion.value}" is not in allowed values and no similar match found.
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          if (confirm(`Accept "${aiSuggestion.value}" as a valid value for ${attrName}? This will add it to the allowed values list for this site.`)) {
                                            handleAcceptEnumValue(
                                              product.id,
                                              attrName,
                                              aiSuggestion.value,
                                              product.category
                                            );
                                          }
                                        }}
                                        disabled={actionLoading}
                                        className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                                      >
                                        Accept "{aiSuggestion.value}" as valid
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAskAI(product, attrName)}
                            disabled={actionLoading || aiSuggestion?.loading}
                            className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                          >
                            {aiSuggestion?.loading ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4 mr-1" />
                            )}
                            Ask AI
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCorrectDialog(product, attrName)}
                            disabled={actionLoading}
                          >
                            <Edit3 className="h-4 w-4 mr-1" />
                            Correct
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleApprove(product.id, attrName)}
                            disabled={actionLoading}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkUnavailable(product.id, attrName)}
                            disabled={actionLoading}
                            className="border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950"
                            title="Mark this value as unavailable - use when no reliable data can be found"
                          >
                            <Ban className="h-4 w-4 mr-1" />
                            N/A
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4">
            <div className="text-sm text-muted-foreground">
              Showing {pagination.offset + 1} to {Math.min(pagination.offset + pageSize, pagination.total)} of {pagination.total} products
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - pageSize) }))}
                disabled={pagination.offset === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(p => ({ ...p, offset: p.offset + pageSize }))}
                disabled={pagination.offset + pageSize >= pagination.total}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
        </TabsContent>

        {/* Research Tab Content */}
        <TabsContent value="research" className="space-y-6">
          {/* Products Needing Research from AEO Pipeline */}
          {!categoryGapMode && needsResearchTotal > 0 && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2 text-amber-800 dark:text-amber-300">
                      <AlertTriangle className="h-5 w-5" />
                      Products Needing Research
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                        {needsResearchTotal}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="mt-1">
                      These products failed AEO validation due to missing specifications. Click to research and fill in the gaps.
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/content-review?contentType=product_aeo&status=needs_research')}
                    className="text-amber-700 border-amber-300 hover:bg-amber-100 dark:text-amber-300 dark:border-amber-700"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    View All in Content Review
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {needsResearchLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {needsResearchProducts.slice(0, 12).map((product) => (
                      <button
                        key={product.id}
                        className="text-left p-3 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors flex items-start gap-3"
                        onClick={() => {
                          loadProductMissingSpecs(product.id);
                          if (product.missingSpecs.length > 0) {
                            setHighlightedSpec(product.missingSpecs[0]);
                          }
                        }}
                      >
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.productName}
                            className="w-10 h-10 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate text-foreground">{product.productName}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {product.brand} | {product.category}
                          </div>
                          {product.missingSpecs.length > 0 && (
                            <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                              Missing: {product.missingSpecs.slice(0, 2).join(', ')}
                              {product.missingSpecs.length > 2 && ` +${product.missingSpecs.length - 2} more`}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {needsResearchTotal > 12 && (
                  <p className="text-sm text-muted-foreground mt-4 text-center">
                    Showing 12 of {needsResearchTotal} products. Use search or view all in Content Review.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Category Gap Mode Banner */}
          {categoryGapMode && (
            <Alert className="bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
              <Package className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-orange-800 dark:text-orange-200">
                    Knowledge Gap: {categoryGapTotal} products in {categoryGapPath?.split(' > ').pop()}
                    {categoryGapAttribute && ` missing "${categoryGapAttribute}"`}
                  </span>
                  <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                    Select a product below to research and fill in the missing specification
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCategoryGapMode(false);
                    setCategoryGapPath(null);
                    setCategoryGapAttribute(null);
                    setCategoryGapProducts([]);
                    navigate('/research/enrichment-review', { replace: true });
                  }}
                  className="text-orange-700 border-orange-300"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Clear Filter
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Category Gap Product List */}
          {categoryGapMode && categoryGapProducts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Products Missing "{categoryGapAttribute || 'Specifications'}"
                  <Badge variant="outline">{categoryGapProducts.length}</Badge>
                </CardTitle>
                <CardDescription>
                  Click on a product to research and fill in the missing specification
                </CardDescription>
              </CardHeader>
              <CardContent>
                {categoryGapLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {categoryGapProducts.slice(0, 12).map((product) => (
                      <button
                        key={product.id}
                        className="text-left p-3 border rounded-lg hover:bg-muted transition-colors flex items-center gap-3"
                        onClick={() => {
                          loadProductMissingSpecs(product.id);
                          if (categoryGapAttribute) {
                            setHighlightedSpec(categoryGapAttribute);
                          }
                        }}
                      >
                        <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{product.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {product.brand} | {product.category}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {categoryGapProducts.length > 12 && (
                  <p className="text-sm text-muted-foreground mt-4 text-center">
                    Showing 12 of {categoryGapProducts.length} products. Use the search below for more.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Product Search */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5" />
                Find a Product
              </CardTitle>
              <CardDescription>
                Search for any product to see what specifications are missing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by product name or brand..."
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  className="pl-9"
                />
                {productSearchLoading && (
                  <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>

              {/* Search Results Dropdown */}
              {productSearchResults.length > 0 && (
                <div className="mt-2 border rounded-md shadow-lg bg-background max-h-64 overflow-y-auto">
                  {productSearchResults.map((product) => (
                    <button
                      key={product.id}
                      className="w-full text-left px-4 py-3 hover:bg-muted border-b last:border-b-0 flex items-center gap-3"
                      onClick={() => handleSelectProduct(product)}
                    >
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="font-medium">{product.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {product.category} {product.price && `| ${product.price}`}
                        </div>
                      </div>
                      {product.isEnriched && (
                        <Badge variant="secondary" className="text-xs">Enriched</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Loading State */}
          {researchProductLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Selected Product - Missing Specs */}
          {selectedResearchProduct && !researchProductLoading && (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      {selectedResearchProduct.productUrl ? (
                        <a
                          href={cleanProductUrl(selectedResearchProduct.productUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline hover:text-primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {selectedResearchProduct.productName}
                          <ExternalLink className="h-3 w-3 ml-1 inline-block opacity-50" />
                        </a>
                      ) : (
                        selectedResearchProduct.productName
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      <span className="font-medium">{selectedResearchProduct.brand}</span> | {selectedResearchProduct.category}
                      <span className="ml-2 text-xs">({selectedResearchProduct.priceTier} tier)</span>
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {selectedResearchProduct.productUrl && (
                      <a
                        href={cleanProductUrl(selectedResearchProduct.productUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex"
                      >
                        <Button variant="outline" size="sm">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View Product
                        </Button>
                      </a>
                    )}
                    {selectedResearchProduct.aeoGenerationId && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/content-review?id=${selectedResearchProduct.aeoGenerationId}&type=product_aeo`)}
                        className="border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950"
                      >
                        <FileSearch className="h-4 w-4 mr-1" />
                        View AEO
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/research/competitive-benchmark?sourceType=product&sourceId=${selectedResearchProduct.productId}&sourceName=${encodeURIComponent(selectedResearchProduct.productName)}`)}
                      className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                      title="Run competitive benchmark analysis"
                    >
                      <BarChart3 className="h-4 w-4 mr-1" />
                      Benchmark
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedResearchProduct(null);
                        setResearchAiSuggestions({});
                        setHighlightedSpec(null);
                      }}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  </div>
                </div>

                {/* AEO Staleness Warning */}
                {selectedResearchProduct.aeoIsStale && selectedResearchProduct.staleAttributes && selectedResearchProduct.staleAttributes.length > 0 && (
                  <div className="mt-4">
                    <AeoStalenessWarning
                      aeoIsStale={selectedResearchProduct.aeoIsStale}
                      staleAttributes={selectedResearchProduct.staleAttributes}
                      staleContentSections={selectedResearchProduct.staleContentSections || []}
                      onRegenerateAeo={() => selectedResearchProduct.aeoGenerationId && handleRegenerateAeo(selectedResearchProduct.aeoGenerationId)}
                      isRegenerating={regeneratingAeo}
                      aeoGenerationId={selectedResearchProduct.aeoGenerationId}
                    />
                  </div>
                )}

                {/* Completeness Score */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Specification Completeness</span>
                    <span className="font-medium">{selectedResearchProduct.completenessScore}%</span>
                  </div>
                  <Progress value={selectedResearchProduct.completenessScore} className="h-2" />
                  <div className="text-xs text-muted-foreground">
                    {selectedResearchProduct.foundCount} of {selectedResearchProduct.requiredCount} required specs found
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Missing Specs Section */}
                {selectedResearchProduct.missingSpecs.length > 0 ? (
                  <div>
                    <h4 className="font-medium mb-3 text-amber-700 dark:text-amber-400 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Missing Specifications ({selectedResearchProduct.missingSpecs.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedResearchProduct.missingSpecs.map((spec) => {
                        const suggestion = researchAiSuggestions[spec.spec];
                        const isHighlighted = highlightedSpec === spec.spec;

                        return (
                          <div
                            key={spec.spec}
                            id={`spec-${spec.spec}`}
                            className={`p-4 rounded-lg border transition-all ${
                              isHighlighted
                                ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950/30 border-blue-300 dark:border-blue-700'
                                : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-foreground">{spec.displayName}</span>
                                  {getSeverityBadge(spec.severity)}
                                </div>
                                {spec.description && (
                                  <p className="text-sm text-muted-foreground">{spec.description}</p>
                                )}
                                {spec.aliases && spec.aliases.length > 0 && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Also known as: {spec.aliases.join(', ')}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleResearchAskAI(spec)}
                                  disabled={suggestion?.loading || savingSpec === spec.spec}
                                  className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                                >
                                  {suggestion?.loading ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <Sparkles className="h-4 w-4 mr-1" />
                                  )}
                                  Ask AI
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openResearchCorrectDialog(spec.spec, false)}
                                  disabled={savingSpec === spec.spec}
                                >
                                  <Edit3 className="h-4 w-4 mr-1" />
                                  Correct
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleResearchMarkUnavailable(spec.spec)}
                                  disabled={savingSpec === spec.spec}
                                  className="border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950"
                                  title="Mark as unavailable - use when this spec cannot be determined"
                                >
                                  {savingSpec === spec.spec ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <Ban className="h-4 w-4 mr-1" />
                                  )}
                                  N/A
                                </Button>
                              </div>
                            </div>

                            {/* AI Suggestion */}
                            {suggestion?.value && (
                              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded">
                                <div className="flex items-center gap-2 text-sm mb-2">
                                  <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                  <span className="text-blue-800 dark:text-blue-300">
                                    AI Suggestion{suggestion.searchedWeb ? ' (via Web Search)' : ''}:
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="font-mono font-medium text-lg">{suggestion.value}</span>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handleSaveSpec(spec.spec, suggestion)}
                                    disabled={savingSpec === spec.spec}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    {savingSpec === spec.spec ? (
                                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                      <Save className="h-4 w-4 mr-1" />
                                    )}
                                    Save
                                  </Button>
                                </div>
                                {suggestion.searchedWeb && suggestion.source && (
                                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-1">
                                    <Search className="h-3 w-3" />
                                    Source:
                                    {suggestion.source.startsWith('http') ? (
                                      <a
                                        href={suggestion.source.replace(/\?utm_source=openai$/, '')}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline flex items-center gap-1"
                                      >
                                        {suggestion.sourceTitle || 'Web Search Result'}
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    ) : (
                                      <span>{suggestion.source}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* No result message */}
                            {suggestion && !suggestion.loading && !suggestion.value && (
                              <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 rounded text-sm text-muted-foreground">
                                AI could not find this specification{suggestion.searchedWeb ? ' even with web search' : ''}. You may need to research manually.
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-600 dark:text-green-400" />
                    <p className="font-medium text-green-600 dark:text-green-400">All required specifications are present!</p>
                    <p className="text-sm text-muted-foreground mt-1">This product has complete spec data for its category.</p>

                    {/* AEO needs regeneration - specs are complete but AEO is still in needs_research */}
                    {selectedResearchProduct.aeoStatus === 'needs_research' && selectedResearchProduct.aeoGenerationId && (
                      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700 rounded-lg">
                        <div className="flex items-center justify-center gap-2 text-amber-700 dark:text-amber-300 mb-3">
                          <AlertTriangle className="h-5 w-5" />
                          <span className="font-medium">AEO Content Needs Regeneration</span>
                        </div>
                        <p className="text-sm text-amber-600 dark:text-amber-400 mb-4">
                          The specifications are now complete, but the AEO content was generated when specs were missing.
                          Regenerate to create improved content using the complete data.
                        </p>
                        <Button
                          onClick={() => handleRegenerateAeo(selectedResearchProduct.aeoGenerationId!)}
                          disabled={regeneratingAeo}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          {regeneratingAeo ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Regenerate AEO Content
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Pipeline return option */}
                    {pipelineRunId && !selectedResearchProduct.aeoGenerationId && (
                      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                        <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                          Ready to retry this product in the pipeline?
                        </p>
                        <Button
                          onClick={() => navigate(`/pipeline?runId=${pipelineRunId}`)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Return to Pipeline
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Existing Specs Section */}
                {Object.keys(selectedResearchProduct.existingSpecs).length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3 text-green-700 dark:text-green-400 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Existing Specifications ({Object.keys(selectedResearchProduct.existingSpecs).length})
                      <span className="text-xs font-normal text-muted-foreground ml-2">Click to verify or correct</span>
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(selectedResearchProduct.existingSpecs).map(([name, spec]) => {
                        const isExpanded = expandedExistingSpec === name;
                        const aiResult = existingSpecAiResults[name];
                        const currentValue = typeof spec.value === 'object' ? JSON.stringify(spec.value) : String(spec.value);
                        const displayValue = spec.unit && !currentValue.toLowerCase().endsWith(spec.unit.toLowerCase())
                          ? `${currentValue} ${spec.unit}`
                          : currentValue;

                        return (
                          <div
                            key={name}
                            className={`rounded border transition-all ${
                              isExpanded
                                ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-300 dark:border-blue-700'
                                : 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 hover:border-blue-300 dark:hover:border-blue-700 cursor-pointer'
                            }`}
                          >
                            <button
                              type="button"
                              className="w-full p-3 text-left flex items-center justify-between"
                              onClick={() => setExpandedExistingSpec(isExpanded ? null : name)}
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{name}:</span>
                                <span className="font-mono text-sm">{displayValue}</span>
                                {spec.source && (
                                  <Badge variant="outline" className="text-xs">
                                    {spec.source}
                                  </Badge>
                                )}
                              </div>
                              <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            </button>

                            {/* Expanded Actions */}
                            {isExpanded && (
                              <div className="px-3 pb-3 border-t border-blue-200 dark:border-blue-700 pt-3">
                                <div className="flex gap-2 mb-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleVerifyExistingSpec(name, currentValue);
                                    }}
                                    disabled={aiResult?.loading || savingSpec === name}
                                    className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                                  >
                                    {aiResult?.loading ? (
                                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                      <Sparkles className="h-4 w-4 mr-1" />
                                    )}
                                    Verify with AI
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openResearchCorrectDialog(name, true, currentValue);
                                    }}
                                    disabled={savingSpec === name}
                                  >
                                    <Edit3 className="h-4 w-4 mr-1" />
                                    Correct
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResearchMarkUnavailable(name);
                                    }}
                                    disabled={savingSpec === name}
                                    className="border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950"
                                  >
                                    {savingSpec === name ? (
                                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                      <Ban className="h-4 w-4 mr-1" />
                                    )}
                                    N/A
                                  </Button>
                                </div>

                                {/* AI Verification Result */}
                                {aiResult?.value && (
                                  <div className="p-3 bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-700 rounded">
                                    <div className="flex items-center gap-2 text-sm mb-2">
                                      <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                      <span className="text-blue-800 dark:text-blue-300">
                                        AI Found{aiResult.searchedWeb ? ' (via Web Search)' : ''}:
                                      </span>
                                      <span className="font-mono font-medium">{aiResult.value}</span>
                                    </div>

                                    {/* Compare values */}
                                    {aiResult.value.toLowerCase() !== displayValue.toLowerCase() ? (
                                      <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700 rounded">
                                        <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300 mb-2">
                                          <AlertTriangle className="h-4 w-4" />
                                          <span className="font-medium">Value Mismatch Detected</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                                          <div>
                                            <span className="text-muted-foreground">Catalog:</span>{' '}
                                            <span className="font-mono">{displayValue}</span>
                                          </div>
                                          <div>
                                            <span className="text-muted-foreground">AI Found:</span>{' '}
                                            <span className="font-mono font-medium text-blue-600 dark:text-blue-400">{aiResult.value}</span>
                                          </div>
                                        </div>
                                        <div className="flex gap-2">
                                          <Button
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleUpdateExistingSpec(name, aiResult.value, aiResult.numericValue, aiResult.unit);
                                            }}
                                            disabled={savingSpec === name}
                                            className="bg-blue-600 hover:bg-blue-700"
                                          >
                                            {savingSpec === name ? (
                                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                            ) : (
                                              <CheckCircle2 className="h-4 w-4 mr-1" />
                                            )}
                                            Update to {aiResult.value}
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setExistingSpecAiResults(prev => {
                                                const { [name]: _, ...rest } = prev;
                                                return rest;
                                              });
                                            }}
                                          >
                                            Keep {displayValue}
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="mt-2 p-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-700 rounded">
                                        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                                          <CheckCircle2 className="h-4 w-4" />
                                          <span>Value matches AI research - no correction needed</span>
                                        </div>
                                      </div>
                                    )}

                                    {/* Source info */}
                                    {aiResult.searchedWeb && aiResult.source && (
                                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-1">
                                        <Search className="h-3 w-3" />
                                        Source:
                                        {aiResult.source.startsWith('http') ? (
                                          <a
                                            href={aiResult.source.replace(/\?utm_source=openai$/, '')}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:underline flex items-center gap-1"
                                          >
                                            {aiResult.sourceTitle || 'Web Search Result'}
                                            <ExternalLink className="h-3 w-3" />
                                          </a>
                                        ) : (
                                          <span>{aiResult.source}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* No result message - but if we already have a source URL, show it as verified */}
                                {aiResult && !aiResult.loading && !aiResult.value && (
                                  <div className={`p-2 rounded text-sm ${
                                    spec.source?.startsWith('http')
                                      ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300'
                                      : 'bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 text-muted-foreground'
                                  }`}>
                                    {spec.source?.startsWith('http') ? (
                                      <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4" />
                                        <span>Value was sourced from verified URL - no conflicting data found.</span>
                                      </div>
                                    ) : (
                                      <>AI could not verify this specification{aiResult.searchedWeb ? ' even with web search' : ''}. The current value may be correct.</>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* All Attributes with Origin Tracking Section */}
                <AllAttributesSection productId={selectedResearchProduct.productId} />
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {!selectedResearchProduct && !researchProductLoading && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <FileSearch className="h-12 w-12 mx-auto mb-4 text-blue-500" />
                <p className="text-lg font-medium">Search for a product to research</p>
                <p className="text-sm mt-1">Enter a product name above to see what specifications are missing</p>
              </CardContent>
            </Card>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Validation Tools Tab Content */}
        <TabsContent value="validation" className="space-y-6">
          <ValidationToolsTab />
        </TabsContent>

        {/* Feed Sync Tab Content */}
        <TabsContent value="feed-sync" className="space-y-6">
          <FeedSyncPanel />
        </TabsContent>
      </Tabs>

      {/* Correct Value Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Correct Attribute Value</DialogTitle>
            <DialogDescription>
              Enter the correct value for <strong>{selectedAttribute}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Product</Label>
              <div className="text-sm text-muted-foreground">{selectedProduct?.name}</div>
            </div>
            <div>
              <Label>Current Value</Label>
              <div className="text-sm font-mono bg-muted p-2 rounded">
                {selectedProduct && selectedAttribute &&
                  formatAttributeValue(selectedProduct.suspicious_attributes[selectedAttribute])}
              </div>
            </div>
            <div>
              <Label htmlFor="correctedValue">Corrected Value</Label>
              <Input
                id="correctedValue"
                value={correctedValue}
                onChange={(e) => setCorrectedValue(e.target.value)}
                placeholder="Enter correct value..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCorrect} disabled={actionLoading || !correctedValue}>
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Research Mode Correct Value Dialog */}
      <Dialog open={researchCorrectDialogOpen} onOpenChange={setResearchCorrectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{researchCorrectIsExisting ? 'Correct Specification' : 'Enter Specification Value'}</DialogTitle>
            <DialogDescription>
              {researchCorrectIsExisting
                ? `Update the value for "${researchCorrectSpecName}"`
                : `Enter the correct value for "${researchCorrectSpecName}"`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Product</Label>
              <div className="text-sm text-muted-foreground">{selectedResearchProduct?.productName}</div>
            </div>
            {researchCorrectIsExisting && researchCorrectCurrentValue && (
              <div>
                <Label>Current Value</Label>
                <div className="text-sm font-mono bg-muted p-2 rounded">
                  {researchCorrectCurrentValue}
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="researchCorrectedValue">{researchCorrectIsExisting ? 'New Value' : 'Value'}</Label>
              <Input
                id="researchCorrectedValue"
                value={researchCorrectValue}
                onChange={(e) => setResearchCorrectValue(e.target.value)}
                placeholder="Enter value..."
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResearchCorrectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResearchCorrect}
              disabled={savingSpec === researchCorrectSpecName || !researchCorrectValue}
              className="bg-green-600 hover:bg-green-700"
            >
              {savingSpec === researchCorrectSpecName && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail View Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>
                {detailProduct?.product_url ? (
                  <a
                    href={cleanProductUrl(detailProduct.product_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline hover:text-primary inline-flex items-center gap-1"
                  >
                    {detailProduct?.name}
                    <ExternalLink className="h-3 w-3 opacity-50" />
                  </a>
                ) : (
                  detailProduct?.name
                )}
              </DialogTitle>
            </div>
            <DialogDescription>
              {detailProduct?.brand} | {detailProduct?.category}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <h4 className="font-medium mb-2 text-amber-700 dark:text-amber-400 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Suspicious Attributes ({Object.keys(detailProduct?.suspicious_attributes || {}).length})
              </h4>
              <div className="space-y-2">
                {Object.entries(detailProduct?.suspicious_attributes || {}).map(([name, attr]) => (
                  <div key={name} className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800">
                    <div className="font-medium text-foreground">{name}</div>
                    <div className="text-sm font-mono">{formatAttributeValue(attr)}</div>
                    {attr.validation_note && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1">{attr.validation_note}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2 text-green-700 dark:text-green-400 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                All Enriched Attributes ({Object.keys(detailProduct?.all_enriched_attributes || {}).length})
              </h4>
              <div className="space-y-2">
                {Object.entries(detailProduct?.all_enriched_attributes || {}).map(([name, attr]) => (
                  <div key={name} className={`p-3 rounded border ${
                    detailProduct?.suspicious_attributes?.[name]
                      ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
                      : 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800'
                  }`}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{name}</span>
                      {getConfidenceBadge(attr.confidence)}
                    </div>
                    <div className="text-sm font-mono">{formatAttributeValue(attr)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Missing Fields Dialog (Story 358) */}
      {selectedSite && (
        <ExportMissingFieldsDialog
          open={showExportDialog}
          onOpenChange={setShowExportDialog}
          siteId={selectedSite.id}
        />
      )}

      {/* Accepted Values Management Dialog */}
      <Dialog open={acceptedValuesDialogOpen} onOpenChange={setAcceptedValuesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Manage Accepted Values
            </DialogTitle>
            <DialogDescription>
              View and manage site-specific accepted values for attributes. These values override sector-level validation.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {acceptedValuesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : siteAttributeValues.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No site-specific accepted values configured.</p>
                <p className="text-sm mt-2">
                  Use "Accept as valid" on suspicious products to add values.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {siteAttributeValues.map((record) => (
                  <div
                    key={record.id}
                    className="border rounded-lg p-4 bg-card"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium">{record.attribute_name}</h4>
                        {record.product_category && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {record.product_category}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {record.accepted_values.length} site override{record.accepted_values.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>

                    {/* Show canonical values for context */}
                    {record.canonical_values && record.canonical_values.length > 0 && (
                      <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded border border-blue-200 dark:border-blue-800">
                        <p className="text-xs text-blue-700 dark:text-blue-300 font-medium mb-1">
                          Sector-level allowed values:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {record.canonical_values.map((cv) => (
                            <span
                              key={cv}
                              className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded text-xs"
                            >
                              {cv}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Site-specific accepted values */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Site-specific overrides:</p>
                      <div className="flex flex-wrap gap-2">
                        {record.accepted_values.map((value) => {
                          const isInCanonical = record.canonical_values?.some(
                            cv => cv.toLowerCase() === value.toLowerCase()
                          );
                          return (
                            <div
                              key={value}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm ${
                                isInCanonical
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700'
                              }`}
                              title={isInCanonical ? 'Also in sector values' : 'Not in sector values - site-specific override'}
                            >
                              <span>{value}</span>
                              {!isInCanonical && (
                                <AlertTriangle className="h-3 w-3 ml-0.5" />
                              )}
                              <button
                                onClick={() => handleRemoveAcceptedValue(record.id, value, record.attribute_name)}
                                disabled={deletingValue === `${record.id}-${value}`}
                                className="ml-1 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                title="Remove this value"
                              >
                                {deletingValue === `${record.id}-${value}` ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <X className="h-3 w-3" />
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptedValuesDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
